import { useEffect, useState } from 'react'
import { useStaticQuery, graphql } from 'gatsby'
import { ExcelFormulas, APR } from "../utils/excel-formulas";

const useProducts = (settings) => {
  const data = useStaticQuery(graphql`
    query useProduct {
      accounts: allMysqlAccount(filter: { visible: { eq: 1 } }) {
        nodes {
          mysqlId
          name
          type
          prefix_url
          specific_url
          url
          account_fees
          amount_max
          payment_bank
          card_fees
          commitment_period
          interest
          completely_free
          points_to_note
          form_visible
          rating
          mobile_payment
          available_currencies
          own_exchange_office
          opening_fee
          transfer_bank
          transfer_online
          transfer_fees
          interest
          acc_at_this_bank
          personal_account
          wniosker_id
          bank {
            prefix_url
            specific_url
            url
            app{
              name
            }
          }
          mysqlImage {
            childImageSharp {
              gatsbyImageData(width: 153)
            }
          }
        }
      }
      cards: allMysqlCard(filter: { visible: { eq: 1 } }) {
        nodes {
          mysqlId
          name
          prefix_url
          aprc
          specific_url
          url
          limit_max
          free_period
          issuance
          card_operator
          interest
          card_fee
          points_to_note
          rating
          type
          mobile_payments
          wniosker_id
          bank {
            prefix_url
            specific_url
            url
          }
          mysqlImage {
            childImageSharp {
              gatsbyImageData(width: 153)
            }
          }
        }
      }
      credits: allMysqlCredit(filter: { visible: { eq: 1 } }) {
        nodes {
          mysqlId
          name
          prefix_url
          specific_url
          url
          percent
          aprc
          fee_min
          fee_max
          points_to_note
          type
          rating
          amount_min
          amount_max
          time_min
          time_max
          wniosker_id
          bank {
            prefix_url
            specific_url
            url
          }
          mysqlImage {
            childImageSharp {
              gatsbyImageData(width: 153)
            }
          }
        }
      }
      firm: allMysqlFirm(filter: { visible: { eq: 1 } }) {
        nodes {
          mysqlId
          name
          url
          type_main
          dbs
          amount_max
          amount_min
          form_visible
          rating
          prefix_url
          specific_url
          firmtypes {
            mysqlId
            url
          }
          wniosker_id: wniosker
          mysqlImage {
            childImageSharp {
              gatsbyImageData(width: 153)
            }
          }
        }
      }
    }
  `);

  const [costs, setCosts] = useState({});
  
  useEffect(() => {
    if (!settings?.input || (settings.types !== 'firm' && settings.types !== 'all' && !settings.types?.includes('firm'))) 
      return;
      
    (async () => {
      const names = data['firm'].nodes.map(product => {
        const args = product.url.split('/');
        return `slug[]=${args[args.length - 1]}`;
      }).join('&');

      let costs = {};

      for (const response of await Promise.all([
        fetch(`https://cms.ofin.pl/api/json/costs?${names}&amount=${settings.input.amount}&period=${settings.input.time}&time_type=day`),
        fetch(`https://cms.ofin.pl/api/json/costs?${names}&amount=${settings.input.amount}&period=${settings.input.time}&time_type=month`),
      ])) {
        const json = await response.json();

        costs = Object.entries(json.costs).reduce((prev, [url, data]) => {
          if (data?.amount) {
            prev[url] ??= { amount: data.amount, time: data.time };
            prev[url].apr ??= data.apr;
            prev[url].installment ??= data.installment;
            prev[url].free ??= data.free;
            prev[url].cost ??= data.cost;
            prev[url].firm_id ??= data.firm_id;
          }

          return prev;
        }, costs);
      }

      setCosts(costs);
    })();
  }, [data, settings?.subtypes?.firm, settings?.types, settings?.input])

  if (!settings) {
    return {
      objectLists: {},
      arrayLists: [],
    };
  }

  const products = {};
  const types = settings.types === 'all' 
    ? ['firm', 'accounts', 'credits', 'cards'] 
    : [settings.types].flat();
  
  const subtypes = settings.subtypes || '';
  const limit = settings.limit || 9999;
  const filters = settings.filters || {};
  
  for (const type of types) {
    const currentFilter = filters[type] || {};
    const currentSubtype = subtypes[type] || '';

    products[type] = [];
    for (const item of data[type].nodes) {
      switch (type) {
        case 'firm':
          const subTypesRegex = currentSubtype ? new RegExp(`-(${[currentSubtype].flat().join('|')})$`) : null;
          const subTypesTest = subTypesRegex 
            ? !item.firmtypes.some((firmtype) => subTypesRegex.test(firmtype.mysqlId)) 
            : false;

          item.costs = costs[item.url] || {};

          if (
            subTypesTest ||
            (currentFilter.nodb && item.dbs.includes(currentFilter.nodb))
          ) {
            continue;
          }

          if (!settings?.filters?.all?.withoutCalculator) {
            if (
              settings?.input?.amount < item.amount_min ||
              settings?.input?.amount > item.amount_max
            ) {
              continue;
            }
          }
        break;

        case 'credits':
          if (currentSubtype && currentSubtype.toString() !== item.type.toString()) {
            continue;
          }

          if (settings?.filters?.credits?.mysqlId && settings?.filters?.credits?.mysqlId !== item.mysqlId) {
            continue;
          }

          if (!settings?.filters?.all?.withoutCalculator) {
            if (
              settings?.input?.amount < item.amount_min || 
              settings?.input?.amount > item.amount_max || 
              settings?.input?.time < item.time_min || 
              settings?.input?.time > item.time_max
            ) {
              continue;
            }
          }
          
          const percentage = item?.percent > 0 
            ? item.percent / 100
            : 0.01;
          
          if (percentage > 0) {
            const tmp_amount = settings?.input?.amount ?? 0;
            const tmp_time = settings?.input?.time ?? 0;
            const provision = (item?.fee_max ?? item.fee_min ?? 0) / 100;
            const installment = Math.round(ExcelFormulas.PMT(percentage / 12, tmp_time, tmp_amount + tmp_amount * provision) * -100) / 100;
            const apr = Math.round(APR(tmp_time, tmp_amount, installment) * 10000) / 100;
            const total = Math.round(installment * tmp_time * 100) / 100;
            
            item.calculations = {
              amount: tmp_amount,
              apr: apr,
              cost: total,
              time: tmp_time,
              rate: installment,
            };
          }
        break;

        case 'accounts':
          if (currentSubtype && item.type.toString() !== currentSubtype) {
            continue;
          }
        break;
        
        case 'cards':
          if (
            (currentFilter.withoutBanks && item.bank) ||
            (currentFilter.onlyBanks && !item.bank)
          ) {
            continue;
          }
        break;

        default:
          continue;
      }

      item.productType = type;
      item.productSubtypes = Array.isArray(currentSubtype) && item?.type_main 
        ? `${item.type_main}` 
        : currentSubtype;

      products[type].push(item);

      if (products[type].length >= limit) {
        break;
      }
    }
  }

  return {
    objectLists: products,
  };
};

export default useProducts;